import { ReactNode, useState, useEffect, createContext, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useAppSession } from '@/contexts/AppSessionContext';
import { type AppCapability, type Capabilities } from '@/lib/capabilities';
import {
  LayoutDashboard,
  Users,
  FileText,
  LogOut,
  Sparkles,
  FolderKanban,
  Settings,
  Search,
  Inbox,
  ClipboardList,
  Wallet,
  Mail,
  Menu,
  Lock,
  UserCircle,
  Target,
  FileSignature,
  PanelLeftClose,
  PanelLeft,
  Briefcase,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

// Context to share sidebar collapsed state with child components
interface SidebarContextValue {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useSidebar() {
  const context = useContext(SidebarContext);
  if (!context) {
    return { isCollapsed: false, setIsCollapsed: () => {} };
  }
  return context;
}
import { UniversalSearchDialog } from '@/components/search';

import { Badge } from '@/components/ui/badge';
import { usePendingImportsCount } from '@/hooks/usePendingImports';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { AlertBadge } from '@/components/alerts';
import { useAgreementOrgSettings } from '@/hooks/useAgreements';
import { TestTube, X, EyeOff } from 'lucide-react';
import { useDemoMode } from '@/hooks/useDemoMode';
import { usePendingMeetingReviews } from '@/hooks/usePendingMeetingReviews';
import { useAutoRegisterSensitiveData } from '@/hooks/useAutoRegisterSensitiveData';
import { WorldClockBar } from '@/components/dashboard/WorldClockBar';
import { GlobalChatPanel } from '@/components/chat/GlobalChatPanel';
import { OrejaPanel } from '@/components/oreja/OrejaPanel';
import { useOrejaSessions } from '@/hooks/useOrejaSessions';
import { SessionTimeoutDialog } from '@/components/shared/SessionTimeoutDialog';

interface DashboardLayoutProps {
  children: ReactNode;
  capabilitiesOverride?: Capabilities;
  isCapabilitiesLoadingOverride?: boolean;
}
const DashboardShellContext = createContext(false);

interface NavItem {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  requiredCapability?: AppCapability;
  showBadge?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    label: 'Work',
    items: [
      { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { href: '/clients', label: 'Clients', icon: Users },
      { href: '/projects', label: 'Projects', icon: FolderKanban },
    ],
  },
  {
    label: 'Pipeline',
    items: [
      {
        href: '/business-dev',
        label: 'Business Dev',
        icon: Briefcase,
        requiredCapability: 'canViewBusinessDev',
      },
      { href: '/goals', label: 'Goals', icon: Target },
    ],
  },
  {
    label: 'Operations',
    items: [
      { href: '/contracts', label: 'Contracts', icon: FileText },
      {
        href: '/contract-agreements',
        label: 'Agreements',
        icon: FileSignature,
        requiredCapability: 'canManageAgreements',
      },
      { href: '/mini-sows', label: 'Scope Updates', icon: ClipboardList },
      { href: '/meetings', label: 'Meetings', icon: Sparkles },
      { href: '/email-reviews', label: 'Messages', icon: Mail },
    ],
  },
  {
    label: 'Admin',
    items: [
      {
        href: '/budget',
        label: 'Financials',
        icon: Wallet,
        requiredCapability: 'canViewFinancials',
      },
      { href: '/imports', label: 'Imports', icon: Inbox, showBadge: true },
      { href: '/settings', label: 'Settings', icon: Settings },
    ],
  },
];

// Flat list derived from groups for any code that needs it
const navItems: NavItem[] = navGroups.flatMap((g) => g.items);

interface SidebarContentProps {
  onNavClick?: () => void;
  collapsed?: boolean;
  capabilities: Capabilities;
  isCapabilitiesLoading: boolean;
  pendingCount: number | undefined;
  pendingReviewCount: number;
  isDemoMode: boolean;
  toggleDemoMode: () => void;
  canAccessDemoMode: boolean;
  onSearchOpen: () => void;
  onSignOut: () => void;
  onSetCollapsed: (collapsed: boolean) => void;
}

function SidebarContent({
  onNavClick,
  collapsed = false,
  capabilities,
  isCapabilitiesLoading,
  pendingCount,
  pendingReviewCount,
  isDemoMode,
  toggleDemoMode,
  canAccessDemoMode,
  onSearchOpen,
  onSignOut,
  onSetCollapsed,
}: SidebarContentProps) {
  const location = useLocation();
  const logoSrc = '/poco-logo-light-small.png';

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex h-full flex-col">
        {/* Top toolbar */}
        <div className={cn(
          'border-b border-border bg-muted/60 dark:bg-muted/30',
          collapsed
            ? 'px-1 py-1.5 flex flex-col items-center gap-0.5'
            : 'px-2 py-1.5 flex items-center justify-between',
        )}>
          {collapsed ? (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground"
                    onClick={() => { onSearchOpen(); onNavClick?.(); }}
                  >
                    <Search className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">Search (⌘K)</TooltipContent>
              </Tooltip>
              <AlertBadge className="h-7 w-7 [&_svg]:size-3.5" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground"
                    onClick={() => onSetCollapsed(false)}
                  >
                    <PanelLeft className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">Expand sidebar</TooltipContent>
              </Tooltip>
            </>
          ) : (
            <>
              <div className="flex items-center gap-0.5">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground"
                      onClick={() => { onSearchOpen(); onNavClick?.(); }}
                    >
                      <Search className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Search (⌘K)</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to="/profile"
                      onClick={onNavClick}
                      className={cn(
                        'flex items-center justify-center h-7 w-7 rounded-md transition-colors',
                        location.pathname === '/profile'
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                      )}
                    >
                      <UserCircle className="h-3.5 w-3.5" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Profile</TooltipContent>
                </Tooltip>

                {canAccessDemoMode && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={isDemoMode ? 'default' : 'ghost'}
                        size="icon"
                        className={cn(
                          'h-7 w-7',
                          isDemoMode && 'bg-violet-600 hover:bg-violet-700 text-white',
                        )}
                        onClick={toggleDemoMode}
                      >
                        <EyeOff className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {isDemoMode ? 'Demo Mode ON' : 'Demo Mode'}
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>

              <div className="flex items-center gap-0.5">
                <AlertBadge className="h-7 w-7 [&_svg]:size-3.5" />

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground"
                      onClick={() => onSetCollapsed(true)}
                    >
                      <PanelLeftClose className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Collapse sidebar</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      data-testid="logout-button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground"
                      onClick={() => { onSignOut(); onNavClick?.(); }}
                    >
                      <LogOut className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Sign out</TooltipContent>
                </Tooltip>
              </div>
            </>
          )}
        </div>

        {/* Logo */}
        <div className={cn(
          'flex flex-col items-center py-3',
          collapsed ? 'px-2' : 'px-4',
        )}>
          <img src={logoSrc} alt="Poco" className={cn(
            'rounded-lg object-cover',
            collapsed ? 'h-9 w-9' : 'h-16 w-16',
          )} />
          {!collapsed && (
            <span className="font-display text-base font-semibold text-foreground mt-1">Poco</span>
          )}
        </div>

        {/* Navigation */}
        <nav className={cn('flex-1 space-y-3 py-1', collapsed ? 'px-2' : 'px-3')}>
          {navGroups.map((group) => {
            const visibleItems = group.items.filter(
              (item) =>
                !item.requiredCapability ||
                capabilities[item.requiredCapability] ||
                isCapabilitiesLoading,
            );
            if (visibleItems.length === 0) return null;

            return (
              <div key={group.label} className="space-y-1">
                {!collapsed && (
                  <div className="px-3 py-1">
                    <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                      {group.label}
                    </span>
                  </div>
                )}
                {visibleItems.map((item) => {
                  const showRoleSkeleton =
                    !!item.requiredCapability &&
                    !capabilities[item.requiredCapability] &&
                    isCapabilitiesLoading;

                  if (showRoleSkeleton) {
                    if (collapsed) {
                      return (
                        <div
                          key={`${item.href}-skeleton`}
                          className="h-9 w-full rounded-lg bg-muted/70 animate-pulse"
                          aria-hidden
                        />
                      );
                    }

                    return (
                      <div
                        key={`${item.href}-skeleton`}
                        className="h-9 rounded-lg bg-muted/70 animate-pulse"
                        aria-hidden
                      />
                    );
                  }

                  const isActive =
                    location.pathname === item.href ||
                    (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
                  const showBadge = item.showBadge && pendingCount && pendingCount > 0;
                  const isMeetingsBadge = item.href === '/meetings' && pendingReviewCount > 0;
                  const badgeCount = showBadge ? pendingCount : isMeetingsBadge ? pendingReviewCount : 0;
                  const hasCapabilityGate = !!item.requiredCapability;

                  const linkContent = (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={onNavClick}
                      className={cn(
                        'flex items-center rounded-lg text-sm font-medium transition-colors',
                        collapsed ? 'justify-center px-2 py-2' : 'gap-1.5 px-3 py-1',
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                      )}
                    >
                      {collapsed && <item.icon className="h-4 w-4 flex-shrink-0" />}
                      {!collapsed && (
                        <>
                          <span>{item.label}</span>
                          {hasCapabilityGate && (
                            <Lock className="h-3 w-3 opacity-40" aria-label="Manager+ only" />
                          )}
                          {(showBadge || isMeetingsBadge) && (
                            <Badge variant="secondary" className="ml-auto h-5 min-w-[20px] px-1.5 text-xs">
                              {badgeCount}
                            </Badge>
                          )}
                        </>
                      )}
                      {collapsed && (showBadge || isMeetingsBadge) && (
                        <Badge
                          variant="secondary"
                          className="absolute -top-1.5 -right-1.5 h-4 min-w-[16px] px-1 text-[10px] leading-none flex items-center justify-center"
                        >
                          {badgeCount > 99 ? '99+' : badgeCount}
                        </Badge>
                      )}
                    </Link>
                  );

                  if (collapsed) {
                    return (
                      <Tooltip key={item.href}>
                        <TooltipTrigger asChild>
                          <div className="relative">{linkContent}</div>
                        </TooltipTrigger>
                        <TooltipContent side="right">{item.label}</TooltipContent>
                      </Tooltip>
                    );
                  }

                  return linkContent;
                })}
              </div>
            );
          })}
        </nav>

      </div>
    </TooltipProvider>
  );
}

function DashboardLayoutShell({
  children,
  capabilitiesOverride,
  isCapabilitiesLoadingOverride,
}: DashboardLayoutProps) {
  const { signOut, user, capabilities, isCapabilitiesLoading, sessionStatus, extendSession } = useAppSession();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [askPocoOpen, setAskPocoOpen] = useState(false);
  const [askPocoMessage, setAskPocoMessage] = useState<string>();
  const [orejaOpen, setOrejaOpen] = useState(false);
  const { badgeCount: orejaBadgeCount, isProcessing: orejaIsProcessing } = useOrejaSessions();
  const { data: pendingCount } = usePendingImportsCount();
  const { data: pendingReviews } = usePendingMeetingReviews();
  const pendingReviewCount = pendingReviews?.length ?? 0;
  const { settings: agreementSettings } = useAgreementOrgSettings();
  const [testModeDismissed, setTestModeDismissed] = useState(false);
  const isTestMode = agreementSettings?.test_mode_enabled && !testModeDismissed;

  // Auto-register all client and project names for demo mode masking
  useAutoRegisterSensitiveData();

  // Demo mode for presentations
  const { isDemoMode, toggleDemoMode, canAccessDemoMode } = useDemoMode();
  const resolvedCapabilities = capabilitiesOverride ?? capabilities;
  const resolvedCapabilitiesLoading = isCapabilitiesLoadingOverride ?? isCapabilitiesLoading;

  // Logo path (using light version for both themes)
  const logoSrc = '/poco-logo-light-small.png';

  // Sidebar collapsed state (persisted in localStorage, default collapsed)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem('sidebarCollapsed');
    // Default to collapsed if no preference stored
    return stored === null ? true : stored === 'true';
  });

  const handleSetCollapsed = (collapsed: boolean) => {
    setIsCollapsed(collapsed);
    localStorage.setItem('sidebarCollapsed', String(collapsed));
  };



  // Keyboard shortcut for search (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Update favicon based on theme
  useEffect(() => {
    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (favicon) {
      favicon.href = logoSrc;
    }
  }, [logoSrc]);

  const handleSignOut = async () => {
    // Clear any pending 2FA state
    localStorage.removeItem('pending2FA');
    await signOut();
    // Force full page navigation to prevent race condition with React state
    window.location.href = '/auth';
  };

  const handleTimeoutSignOut = async () => {
    localStorage.removeItem('pending2FA');
    await signOut();
    const currentPath = window.location.pathname + window.location.search;
    const shouldRedirect = !currentPath.startsWith('/auth') && !currentPath.startsWith('/invite');
    const authUrl = shouldRedirect && currentPath !== '/'
      ? `/auth?redirect=${encodeURIComponent(currentPath)}`
      : '/auth';
    window.location.href = authUrl;
  };

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  const sidebarProps = {
    capabilities: resolvedCapabilities,
    isCapabilitiesLoading: resolvedCapabilitiesLoading,
    pendingCount,
    pendingReviewCount,
    isDemoMode,
    toggleDemoMode,
    canAccessDemoMode,
    onSearchOpen: () => setSearchOpen(true),
    onSignOut: handleSignOut,
    onSetCollapsed: handleSetCollapsed,
  };

  return (
    <DashboardShellContext.Provider value={true}>
      <div className="min-h-screen bg-background">
      {/* Test Mode Banner */}
      {isTestMode && (
        <div className="fixed inset-x-0 top-0 z-[70] bg-amber-500 text-amber-950 py-1.5 px-4 flex items-center justify-center gap-2 text-sm font-medium">
          <TestTube className="h-4 w-4" />
          <span>
            Test Mode Active - Agreement emails are being redirected to{' '}
            {agreementSettings?.test_mode_recipient_override || 'test recipient'}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 hover:bg-amber-600/20 ml-2"
            onClick={() => setTestModeDismissed(true)}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* Mobile Header */}
      <header
        className={cn(
          'fixed inset-x-0 z-[60] flex h-16 items-center justify-between border-b border-border bg-card px-4 md:hidden',
          isTestMode ? 'top-8' : 'top-0',
        )}
      >
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open navigation menu"
          >
            <Menu className="h-6 w-6" />
          </Button>
          <div className="flex items-center gap-2">
            <img src={logoSrc} alt="Poco" className="h-8 w-8 rounded-lg object-cover" />
            <span className="font-display text-xl font-semibold text-foreground">Poco</span>
          </div>
        </div>
        <AlertBadge />
      </header>

      {/* Desktop Sidebar */}
      <aside
        data-testid="sidebar"
        className={cn(
          'fixed left-0 z-[60] hidden border-r border-border bg-card md:block transition-all duration-200',
          isCollapsed ? 'w-16' : 'w-52',
          isTestMode ? 'top-8 bottom-0' : 'inset-y-0',
        )}
      >
        <SidebarContent {...sidebarProps} collapsed={isCollapsed} />
      </aside>

      {/* Mobile Sidebar Sheet */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="w-52 p-0">
          <SidebarContent {...sidebarProps} onNavClick={handleNavClick} />
        </SheetContent>
      </Sheet>

      {/* Main content */}
      <SidebarContext.Provider value={{ isCollapsed, setIsCollapsed: handleSetCollapsed }}>
        <main
          className={cn(
            'transition-all duration-200',
            isCollapsed ? 'md:pl-16' : 'md:pl-52',
            isTestMode ? 'pt-24 md:pt-8' : 'pt-16 md:pt-0',
          )}
        >
          <div className="container py-8 pb-14">{children}</div>
        </main>
        <WorldClockBar
          onAskPoco={() => setAskPocoOpen(true)}
          onOreja={() => setOrejaOpen(true)}
          orejaBadgeCount={orejaBadgeCount}
          orejaIsProcessing={orejaIsProcessing}
        />
      </SidebarContext.Provider>

      {/* Universal Search Dialog */}
      <UniversalSearchDialog
        open={searchOpen}
        onOpenChange={setSearchOpen}
        onAskPoco={(query) => {
          setAskPocoMessage(query);
          setAskPocoOpen(true);
        }}
      />
      <GlobalChatPanel
        open={askPocoOpen}
        onOpenChange={(open) => {
          setAskPocoOpen(open);
          if (!open) setAskPocoMessage(undefined);
        }}
        initialMessage={askPocoMessage}
      />
      <OrejaPanel open={orejaOpen} onOpenChange={setOrejaOpen} />
      <SessionTimeoutDialog
        sessionStatus={sessionStatus}
        onExtendSession={extendSession}
        onSignOut={handleTimeoutSignOut}
      />
      </div>
    </DashboardShellContext.Provider>
  );
}

export function DashboardLayout(props: DashboardLayoutProps) {
  const inDashboardShell = useContext(DashboardShellContext);
  if (inDashboardShell) {
    return <>{props.children}</>;
  }
  return <DashboardLayoutShell {...props} />;
}
